import type { Permission } from "./access";
import type { GatedModule } from "./modules";

/**
 * **Workflows — the catalogue both sides read** (WG-D1–WG-D19, STORY-223).
 *
 * A workflow is a Start box and steps joined by arrows; a step may fork or wait;
 * each arrow carries a condition that stops the run when false (WG-D4). It acts
 * on any record type registered here (WG-D1), with any action a person could
 * take (WG-D2), and never messages a patient (WG-D3).
 *
 * This file is the *shape* of the catalogue — which record types exist, their
 * fields, the records they link to and their actions — so the editor and the
 * engine cannot disagree. How a record is loaded and how an action runs is the
 * server's (`apps/api/src/workflows/catalogue.ts`). **Adding a record type is a
 * row here and a loader there; the engine does not change** (WG-D1).
 */

/** The record types a workflow can act on — the first build is the ring's things (WG-D1). */
export const WORKFLOW_SUBJECTS = [
  "booking",
  "patient",
  "tooth",
  "procedure",
  "item",
  "staff",
  "timeoff",
  "task",
  "expense",
] as const;
export type WorkflowSubject = (typeof WORKFLOW_SUBJECTS)[number];

export function isWorkflowSubject(value: string): value is WorkflowSubject {
  return (WORKFLOW_SUBJECTS as readonly string[]).includes(value);
}

export type WorkflowFieldType = "enum" | "number" | "money" | "bool" | "date" | "text";

export interface WorkflowFieldDef {
  key: string;
  label: string;
  type: WorkflowFieldType;
  /** For `enum`: the values, with the words a person reads. */
  options?: { value: string; label: string }[];
  /**
   * The database column, for a field read straight off the row — only these can
   * be named in an event's *when [field] becomes [value]* (WG-D5), because the
   * event carries the row before and after the change and nothing else.
   */
  column?: string;
}

/** A record linked to the subject, whose fields a condition may read and an action may act on (WG-D8). */
export interface WorkflowLinkDef {
  key: string;
  label: string;
  to: WorkflowSubject;
}

export type WorkflowParamType = "text" | "longtext" | "holder" | "hours";

export interface WorkflowParamDef {
  key: string;
  label: string;
  type: WorkflowParamType;
  required: boolean;
}

export interface WorkflowActionDef {
  /** The same key the quick ring uses for the action on this thing, so a ring run opens its dialog (WF-D3). */
  key: string;
  label: string;
  /** `auto` runs on its own; `person` is asked for in a ring run and becomes a task otherwise (WG-D10). */
  kind: "auto" | "person";
  needs: Permission[];
  module?: GatedModule;
  /** What a step of this action needs to be told — a task's title, a reason. */
  params?: WorkflowParamDef[];
  /** It creates a record a later step may act on (WG-D8). */
  makes?: WorkflowSubject;
  danger?: boolean;
}

export interface WorkflowSubjectDef {
  /** "a booking", for "When a booking changes". */
  label: string;
  /** "Booking", for headings. */
  title: string;
  fields: WorkflowFieldDef[];
  links: WorkflowLinkDef[];
  actions: WorkflowActionDef[];
  /** Records a schedule can be limited to — bookings by day (WG-D5). */
  windows?: { value: string; label: string }[];
}

const STATUS = [
  { value: "NOT_CHECKED_IN", label: "Not checked in" },
  { value: "CONFIRMED", label: "Confirmed" },
  { value: "CHECKED_IN", label: "Checked in" },
  { value: "IN_CHAIR", label: "In chair" },
  { value: "COMPLETED", label: "Completed" },
  { value: "NO_SHOW", label: "No-show" },
  { value: "CANCELLED", label: "Cancelled" },
];

/** Every record type can raise a task — the one way a workflow reaches a person (WG-D3). */
const CREATE_TASK: WorkflowActionDef = {
  key: "task",
  label: "Create a task",
  kind: "auto",
  needs: [],
  module: "TASKS",
  makes: "task",
  params: [
    { key: "title", label: "Title", type: "text", required: true },
    { key: "description", label: "Details", type: "longtext", required: false },
    { key: "holder", label: "For", type: "holder", required: true },
    { key: "dueInHours", label: "Due in (hours)", type: "hours", required: false },
  ],
};

export const WORKFLOW_CATALOGUE: Record<WorkflowSubject, WorkflowSubjectDef> = {
  booking: {
    label: "a booking",
    title: "Booking",
    fields: [
      { key: "status", label: "Status", type: "enum", options: STATUS, column: "status" },
      { key: "date", label: "Date", type: "date" },
      { key: "procedure", label: "Procedure", type: "text" },
      { key: "patient.balance", label: "Patient owes", type: "money" },
      { key: "patient.critical", label: "Patient has a critical alert", type: "bool" },
      { key: "patient.hasFuture", label: "Patient has a future booking", type: "bool" },
    ],
    links: [{ key: "patient", label: "Its patient", to: "patient" }],
    windows: [
      { value: "today", label: "Every booking today" },
      { value: "tomorrow", label: "Every booking tomorrow" },
      { value: "yesterday", label: "Every booking yesterday" },
    ],
    actions: [
      { key: "confirm", label: "Confirm", kind: "auto", needs: ["appointments.status"] },
      { key: "checkin", label: "Check in", kind: "auto", needs: ["appointments.status"] },
      { key: "seat", label: "Start treatment", kind: "auto", needs: ["appointments.status"] },
      {
        key: "noshow",
        label: "Mark no-show",
        kind: "auto",
        needs: ["appointments.status"],
        danger: true,
      },
      {
        key: "cancel",
        label: "Cancel",
        kind: "auto",
        needs: ["appointments.status", "appointments.cancel"],
        danger: true,
      },
      CREATE_TASK,
      { key: "complete", label: "Complete visit", kind: "person", needs: ["appointments.status"] },
      { key: "pay", label: "Take payment", kind: "person", needs: ["billing.payment.record"] },
      { key: "move", label: "Reschedule", kind: "person", needs: ["appointments.move"] },
      { key: "next", label: "Book next visit", kind: "person", needs: ["appointments.book"] },
      {
        key: "note",
        label: "Write a clinical note",
        kind: "person",
        needs: ["clinical.notes.write"],
      },
    ],
  },
  patient: {
    label: "a patient",
    title: "Patient",
    fields: [
      { key: "balance", label: "Owes", type: "money" },
      { key: "critical", label: "Has a critical alert", type: "bool" },
      { key: "hasFuture", label: "Has a future booking", type: "bool" },
      { key: "recallDue", label: "Recall due", type: "date" },
      { key: "createdAt", label: "File opened", type: "date" },
    ],
    links: [],
    actions: [
      CREATE_TASK,
      { key: "book", label: "Book appointment", kind: "person", needs: ["appointments.book"] },
      { key: "pay", label: "Take payment", kind: "person", needs: ["billing.payment.record"] },
      { key: "flag", label: "Add a flag", kind: "person", needs: ["patients.flags.manage"] },
      { key: "edit", label: "Edit details", kind: "person", needs: ["patients.edit"] },
      { key: "note", label: "New note", kind: "person", needs: ["clinical.notes.write"] },
    ],
  },
  tooth: {
    label: "a charted tooth",
    title: "Tooth",
    fields: [
      {
        key: "status",
        label: "Status",
        type: "enum",
        column: "status",
        options: [
          { value: "PLANNED", label: "To be done" },
          { value: "COMPLETED", label: "Done" },
          { value: "CANCELLED", label: "Cancelled" },
        ],
      },
      { key: "tooth", label: "Tooth number", type: "number", column: "fdi_number" },
      { key: "procedure", label: "Procedure", type: "text", column: "label" },
    ],
    links: [{ key: "patient", label: "Its patient", to: "patient" }],
    actions: [
      CREATE_TASK,
      { key: "markdone", label: "Mark done", kind: "person", needs: ["clinical.chart.edit"] },
      { key: "open", label: "Open the tooth", kind: "person", needs: ["clinical.chart.view"] },
    ],
  },
  procedure: {
    label: "a procedure",
    title: "Procedure",
    fields: [
      { key: "price", label: "Price", type: "money" },
      { key: "visits", label: "Visits", type: "number" },
      { key: "retired", label: "Retired", type: "bool" },
    ],
    links: [],
    actions: [
      CREATE_TASK,
      { key: "edit", label: "Price and visits", kind: "person", needs: ["procedures.manage"] },
    ],
  },
  item: {
    label: "an inventory item",
    title: "Inventory item",
    fields: [
      { key: "onHand", label: "On hand (uses)", type: "number", column: "quantity_on_hand" },
      { key: "unopened", label: "Unopened packs", type: "number" },
      { key: "low", label: "Running low", type: "bool" },
      { key: "lowAt", label: "Low threshold (uses)", type: "number", column: "low_at" },
    ],
    links: [],
    actions: [
      {
        key: "order",
        label: "Add to cart",
        kind: "auto",
        needs: ["inventory.movement.record"],
        module: "SUPPLY",
      },
      CREATE_TASK,
      {
        key: "add",
        label: "Add stock",
        kind: "person",
        needs: ["inventory.movement.record"],
        module: "SUPPLY",
      },
      {
        key: "remove",
        label: "Remove stock",
        kind: "person",
        needs: ["inventory.movement.record"],
        module: "SUPPLY",
      },
    ],
  },
  staff: {
    label: "a member of staff",
    title: "Staff",
    fields: [
      {
        key: "job",
        label: "Job",
        type: "enum",
        column: "role",
        options: [
          { value: "ADMIN", label: "Administrator" },
          { value: "DENTIST", label: "Dentist" },
          { value: "HYGIENIST", label: "Hygienist" },
          { value: "ASSISTANT", label: "Assistant" },
          { value: "FRONT_DESK", label: "Front desk" },
        ],
      },
      { key: "active", label: "Active", type: "bool", column: "is_active" },
    ],
    links: [],
    actions: [
      CREATE_TASK,
      { key: "week", label: "Edit every week", kind: "person", needs: ["staff.rota.edit.any"] },
      { key: "sick", label: "Off sick", kind: "person", needs: ["hr.absence.record"] },
    ],
  },
  timeoff: {
    label: "a time-off request",
    title: "Time off",
    fields: [
      {
        key: "status",
        label: "Status",
        type: "enum",
        column: "status",
        options: [
          { value: "PENDING", label: "Waiting" },
          { value: "APPROVED", label: "Approved" },
          { value: "DECLINED", label: "Declined" },
        ],
      },
      {
        key: "type",
        label: "Kind",
        type: "enum",
        column: "type",
        options: [
          { value: "ANNUAL", label: "Annual" },
          { value: "SICK", label: "Sick" },
          { value: "UNPAID", label: "Unpaid" },
          { value: "OTHER", label: "Other" },
        ],
      },
      { key: "starts", label: "Starts", type: "date" },
      { key: "days", label: "Days", type: "number" },
    ],
    links: [{ key: "staff", label: "Who asked", to: "staff" }],
    actions: [
      CREATE_TASK,
      // A person's step: deciding leave is a person's decision with a coverage
      // check and a "never your own" rule (QF-D6) a role cannot answer for.
      { key: "approve", label: "Approve", kind: "person", needs: ["staff.timeoff.decide"] },
      {
        key: "decline",
        label: "Decline",
        kind: "person",
        needs: ["staff.timeoff.decide"],
        danger: true,
      },
    ],
  },
  task: {
    label: "a task",
    title: "Task",
    fields: [
      {
        key: "status",
        label: "Status",
        type: "enum",
        column: "status",
        options: [
          { value: "OPEN", label: "Open" },
          { value: "RESOLVED", label: "Resolved" },
          { value: "CLOSED", label: "Closed" },
          { value: "AUTO_CLOSED", label: "Closed by itself" },
        ],
      },
      { key: "due", label: "Due", type: "date" },
      { key: "title", label: "Title", type: "text", column: "title" },
    ],
    links: [],
    actions: [
      { key: "resolve", label: "Resolve", kind: "auto", needs: [], module: "TASKS" },
      {
        key: "close",
        label: "Close",
        kind: "auto",
        needs: [],
        module: "TASKS",
        danger: true,
        params: [{ key: "reason", label: "Reason", type: "text", required: true }],
      },
      CREATE_TASK,
    ],
  },
  expense: {
    label: "an expense",
    title: "Expense",
    fields: [
      { key: "amount", label: "Amount", type: "money" },
      { key: "date", label: "Date", type: "date" },
      { key: "category", label: "Category", type: "text" },
    ],
    links: [],
    actions: [
      CREATE_TASK,
      {
        key: "edit",
        label: "Edit",
        kind: "person",
        needs: ["accounting.manage"],
        module: "ACCOUNTING",
      },
    ],
  },
};

// ── Conditions (WG-D6) ─────────────────────────────────────────────────────

export type WorkflowOp =
  | "is"
  | "isNot"
  | "eq"
  | "ne"
  | "lt"
  | "gt"
  | "lte"
  | "gte"
  | "yes"
  | "no"
  | "today"
  | "beforeToday"
  | "afterToday"
  | "withinDays"
  | "olderThanDays"
  | "contains"
  | "empty"
  | "notEmpty";

/** The rules each field type offers, with the words the editor shows. */
export const WORKFLOW_OPS: Record<WorkflowFieldType, { op: WorkflowOp; label: string }[]> = {
  enum: [
    { op: "is", label: "is" },
    { op: "isNot", label: "is not" },
  ],
  number: [
    { op: "eq", label: "is" },
    { op: "ne", label: "is not" },
    { op: "lt", label: "is below" },
    { op: "gt", label: "is above" },
    { op: "lte", label: "is at most" },
    { op: "gte", label: "is at least" },
  ],
  money: [
    { op: "gt", label: "is above" },
    { op: "lt", label: "is below" },
    { op: "eq", label: "is" },
    { op: "gte", label: "is at least" },
    { op: "lte", label: "is at most" },
  ],
  bool: [
    { op: "yes", label: "is yes" },
    { op: "no", label: "is no" },
  ],
  date: [
    { op: "today", label: "is today" },
    { op: "beforeToday", label: "is before today" },
    { op: "afterToday", label: "is after today" },
    { op: "withinDays", label: "is within the next … days" },
    { op: "olderThanDays", label: "is more than … days ago" },
  ],
  text: [
    { op: "is", label: "is" },
    { op: "contains", label: "contains" },
    { op: "empty", label: "is empty" },
    { op: "notEmpty", label: "is not empty" },
  ],
};

/** Rules that take no value. */
export const VALUELESS_OPS: readonly WorkflowOp[] = [
  "yes",
  "no",
  "today",
  "beforeToday",
  "afterToday",
  "empty",
  "notEmpty",
];

/** The pseudo-field *Only if the last step was done* — never on the first arrow. */
export const LAST_STEP_DONE = "lastDone";

/**
 * One condition: a field of the record, a rule and a value (WG-D6). `null` on
 * an arrow is *Always*. Money is in minor units, as everywhere (CLAUDE.md).
 */
export interface WorkflowCondition {
  field: string;
  op: WorkflowOp;
  value?: string | number | null;
}

// ── The graph (WG-D4) ──────────────────────────────────────────────────────

/** Which record a step acts on (WG-D8): the start record, a linked one, or one an earlier step made. */
export type WorkflowTarget = "self" | `link:${string}` | `made:${string}`;

export type WorkflowWait =
  | { kind: "for"; amount: number; unit: "minutes" | "hours" | "days" }
  | { kind: "until"; at: string; day: "today" | "tomorrow" }
  | {
      kind: "field";
      condition: WorkflowCondition;
      giveUp: { amount: number; unit: "hours" | "days" };
    };

export type WorkflowHolder = { roleId: string } | { userId: string };

export interface WorkflowNode {
  id: string;
  kind: "start" | "action" | "fork" | "wait";
  /** Where the box sits on the canvas; only *Tidy up* and *Save* move it (WF-D8). */
  x: number;
  y: number;
  action?: string;
  target?: WorkflowTarget;
  params?: Record<string, string | number | WorkflowHolder | null>;
  /** A step someone does: the role its task goes to outside a ring run (WG-D10). */
  roleId?: string | null;
  /** A fork's question (WG-D4). */
  condition?: WorkflowCondition;
  wait?: WorkflowWait;
}

export interface WorkflowEdge {
  id: string;
  from: string;
  to: string;
  /** Out of a fork: which answer this arrow is. */
  branch?: "yes" | "no";
  /** `null` is *Always*. Never set on an arrow out of a fork. */
  condition: WorkflowCondition | null;
}

export interface WorkflowGraph {
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
}

// ── Starts (WG-D5) ─────────────────────────────────────────────────────────

export type WorkflowTrigger =
  | { kind: "ring" }
  | {
      kind: "event";
      on: "created" | "changed" | "deleted" | "any";
      /** Optional: only when this field (a column field) becomes `value`. */
      field?: string | null;
      value?: string | number | null;
    }
  | {
      kind: "schedule";
      repeat: "day" | "weekday" | "monday" | "month";
      /** "HH:MM", in each location's own time (WG-D11). */
      at: string;
      /** For bookings: which day's (`today`…). Other types run over every live record. */
      window?: string | null;
    };

export const WORKFLOW_REPEATS: { value: string; label: string }[] = [
  { value: "day", label: "Every day" },
  { value: "weekday", label: "Every weekday" },
  { value: "monday", label: "Every Monday" },
  { value: "month", label: "On the 1st of each month" },
];

/** The most links one chain of workflows may have before it stops (WG-D9). */
export const WORKFLOW_CHAIN_LIMIT = 5;
/** How long the run log keeps a run (WG-D17). */
export const WORKFLOW_LOG_DAYS = 180;

// ── Over the wire ──────────────────────────────────────────────────────────

export interface WorkflowInput {
  name: string;
  subject: WorkflowSubject;
  trigger: WorkflowTrigger;
  enabled: boolean;
  /** Empty with `allBranches` true: every location (WG-D11). */
  allBranches: boolean;
  branchIds: string[];
  /** Who sees it in their ring and on the page (WF-D5). Empty is everyone. */
  roleIds: string[];
  /** Whose permissions an unattended run has (WG-D7). Required unless it starts from the ring. */
  runAsRoleId: string | null;
  graph: WorkflowGraph;
}

export type WorkflowRunStatus =
  | "RUNNING"
  | "WAITING_PERSON"
  | "WAITING_TASK"
  | "WAITING_TIME"
  | "WAITING_FIELD"
  | "DONE"
  | "STOPPED"
  | "FAILED"
  | "CANCELLED";

export interface WorkflowRunSummary {
  id: string;
  status: WorkflowRunStatus;
  recordName: string;
  outcome: string | null;
  startedAt: string;
  finishedAt: string | null;
}

export interface WorkflowView extends WorkflowInput {
  id: string;
  version: number;
  updatedAt: string;
  /** The last run per record, newest first, a handful (WF-D10). */
  lastRuns: WorkflowRunSummary[];
  openRuns: number;
}

export interface WorkflowSettingsView {
  /** *Pause all workflows* (WG-D16). */
  paused: boolean;
  pausedAt: string | null;
}

export interface WorkflowRunStepView {
  nodeId: string;
  label: string;
  result: "DONE" | "SKIPPED" | "STOPPED" | "TASK" | "WAITING" | "FAILED" | "FORK" | "ASKED";
  message: string;
  at: string;
}

/** A run as the ring's panel shows it (WF-D3). */
export interface WorkflowRunView {
  id: string;
  workflowId: string;
  workflowName: string;
  status: WorkflowRunStatus;
  recordName: string;
  /** Set while it waits for the person at the screen: the step to do. */
  asking: {
    nodeId: string;
    action: string;
    label: string;
    subject: WorkflowSubject;
    recordId: string;
  } | null;
  outcome: string | null;
  steps: WorkflowRunStepView[];
}

/** What *Test run* would do to each record, changing nothing (WF-D6). */
export interface WorkflowTestLine {
  recordName: string;
  outcome: string;
}

/** A workflow as a ring wedge (WG-D18). */
export interface RingWorkflowView {
  id: string;
  name: string;
  subject: WorkflowSubject;
  stepCount: number;
}
